import { Navigate, useLocation } from 'react-router';

import { useCurrentUser, useProfiles } from '@chartai/api-client';

import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';
import { useProfileStore } from '@/shared/stores/useProfileStore';

// Routes inside /dashboard that are safe to render even without a profile.
const PROFILE_EXEMPT_PATHS = ['/dashboard/profiles', '/dashboard/billing', '/dashboard/settings'];

interface ProfileGuardProps {
  children: React.ReactNode;
}

export function ProfileGuard({ children }: ProfileGuardProps) {
  const { user, isLoading } = useCurrentUser();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner size="lg" className="min-h-screen" />;
  }

  if (!user) {
    // AuthGuard above us handles the unauthenticated redirect.
    return null;
  }

  if (PROFILE_EXEMPT_PATHS.some((p) => location.pathname.startsWith(p))) {
    return <>{children}</>;
  }

  return <ProfileGuardInner uid={user.uid}>{children}</ProfileGuardInner>;
}

interface ProfileGuardInnerProps {
  uid: string;
  children: React.ReactNode;
}

function ProfileGuardInner({ uid, children }: ProfileGuardInnerProps) {
  const { data: profiles, isLoading } = useProfiles(uid);
  const selectedProfile = useProfileStore((state) => state.selectedProfile);

  if (isLoading) {
    return <LoadingSpinner size="lg" className="min-h-screen" />;
  }

  if (!profiles || profiles.length === 0) {
    return <Navigate to="/dashboard/profiles" replace />;
  }

  if (!selectedProfile) {
    return <Navigate to="/profiles/select" replace />;
  }

  return <>{children}</>;
}
